import React, {
  useState,
} from 'react';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import Dialog from 'pages/Dashboard/components/Dialog';
import NavigationTabs from 'pages/Dashboard/components/NavigationTabs';
import {
  MasterTemplateDTO,
  TemplateListItemDTO,
} from 'dtos';
import NewTemplateForm from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/NewTemplateForm';
import ExistingTemplateForm from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/ExistingTemplateForm';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  VisitNoteSOAPSectionNames,
} from 'core/constants';

const actions = ['new', 'use', 'update'] as const;
export type VisitNoteTemplateDialogAction = typeof actions[number];

export type VisitNoteTemplateDialogParams = {
  section?: string;
  content?: string | MasterTemplateDTO;
  onSelect?: ((templateName?: string) => void) | null;
  actions?: VisitNoteTemplateDialogAction[];
};
export type Props = VisitNoteTemplateDialogParams & {
  onClose: () => void;
};

export type TemplateForm = {
  templateName?: string;
  template?: TemplateListItemDTO;
  templates?: TemplateListItemDTO[];
};

type TabProps = {
  hasContent: boolean;
};

const tabs = [{
  label: 'Existing Templates',
  key: 'existing',
  index: 0,
}, {
  label: 'New Template',
  key: 'new',
  index: 1,
  isDisabled: ({ hasContent }: TabProps) => !hasContent,
  disabledTooltip: 'Add content before creating a new template.',
}];

const contentComponents = [
  ExistingTemplateForm,
  NewTemplateForm,
];

const defaultActions: VisitNoteTemplateDialogAction[] = ['new', 'use', 'update'];

const sectionLabels: Record<string, string> = {
  [VisitNoteSOAPSectionNames.AdditionalHPITemplate]: 'Subjective',
  [VisitNoteSOAPSectionNames.PhysicalExamTemplate]: 'Physical Exam',
  [VisitNoteSOAPSectionNames.RadiologyTemplate]: 'Review of Imaging',
  [VisitNoteSOAPSectionNames.AdditionalAssessmentTemplate]: 'Additional Assessment',
  [VisitNoteSOAPSectionNames.PlanTemplate]: 'Plan',
};

export default function VisitNoteTemplateDialog(props: Props) {
  const {
    actions = defaultActions,
    content,
    onClose,
    onSelect,
    section,
  } = props;
  const actionSet = new Set(actions);
  const showNavigation = actionSet.has('new') && (
    actionSet.has('use') || actionSet.has('update')
  );

  const hasContent = !isEmpty(content);
  const usesBulkSectionTemplates = actionSet.has('use')
    && isFunction(onSelect)
    && notEmptyString(section);

  const [active, setActive] = useState<number>(0);
  const showBulkLayout = usesBulkSectionTemplates && active === 0;

  const Component = contentComponents[active];
  const title = actionSet.has('new') ? 'New Template' : 'Existing Templates';
  const sectionLabel = section ? sectionLabels[section] : undefined;
  const dialogTitle = sectionLabel ? `${sectionLabel} Templates` : title;

  return (
    <Dialog
      open
      className={clsx('!space-y-0 min-h-0', {
        '!w-100 !h-56': !showBulkLayout,
        '!w-[48rem] !h-[32rem] max-h-[85vh] gap-2 !overflow-hidden': showBulkLayout,
      })}
      onClose={onClose}
      maxWidth={showBulkLayout ? false : 'xs'}
      title={showNavigation ? (
        <div className="flex flex-col gap-2">
          {sectionLabel && (
            <h2 className="text-base font-semibold text-gray-900">
              {dialogTitle}
            </h2>
          )}
          <NavigationTabs
            className="flex"
            tabs={tabs}
            activeIndex={active}
            onClick={setActive}
            tabData={{ hasContent }}
          />
        </div>
      ) : dialogTitle}
    >
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <Component
          {...props}
          actions={actions}
        />
      </div>
    </Dialog>
  );
}
